/**
 * The motif's web half (app.css «Transitions»): every screen and block swap
 * runs through `swap`, which wraps the commit in a View Transition so the
 * browser snapshots the outgoing pixels and plays echo-sink / echo-rise over
 * them — the pseudo-element rules live in web-compat.css («The motif, snapshot
 * side»). Bare in node, on engines without the API and under reduced motion:
 * the mutation is never hostage to the decoration.
 */
import { flushSync } from "react-dom";
import { currentLifetime, isCurrent } from "../../lib/lifetime";
import type { RuntimeTransitionKind } from "../../store/runtime";

export type SwapKind = RuntimeTransitionKind;

declare global {
  interface Window {
    /** Hands the shadows under `el` back over one beat of the tempo instead of
     * at once (clients/shared/motion.js, D292). */
    __echoSettle?(el: Element): void;
  }
}

let sequence = 0;
let current: ViewTransition | undefined;
/** One coordinator per document; runtime and UI both use this commit barrier. */
export function swap(kind: SwapKind, commit: () => void): void {
  const lifetime = currentLifetime();
  if (
    typeof document === "undefined" ||
    typeof document.startViewTransition !== "function" ||
    (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches)
  ) {
    if (isCurrent(lifetime)) commit();
    return;
  }
  const id = ++sequence;
  current?.skipTransition();
  document.documentElement.dataset["vt"] = kind;
  const transition = document.startViewTransition(() => {
    if (isCurrent(lifetime)) flushSync(commit);
  });
  current = transition;
  void transition.finished
    .catch(() => {})
    .finally(() => {
      if (sequence === id) {
        // The regions it named cast nothing while it played (web-compat.css,
        // D291); their shadows come back in over one beat, not at once. Settle
        // first: its style read, after the attribute is gone, would be the snap.
        window.__echoSettle?.(document.documentElement);
        delete document.documentElement.dataset["vt"];
        current = undefined;
      }
    });
}
